/**
 * API Response Mapper - Generic
 * @description Maps backend API responses to frontend API responses
 */

import { BackendApiResponse } from '@/types/backend.types';
import { ApiResponse, BusinessError } from '@/types/api.types';

export class ApiMapper {
  /**
   * Map backend API response to frontend API response
   * @param backendResponse - Backend API response with shortened data
   * @param dataMapper - Function to map the data from backend to frontend format
   * @returns Frontend API response with full field names
   */
  static mapResponse<TBackend, TFrontend>(
    backendResponse: BackendApiResponse<TBackend>,
    dataMapper: (data: TBackend) => TFrontend
  ): ApiResponse<TFrontend> {
    if (backendResponse.success) {
      return {
        success: true,
        data: dataMapper(backendResponse.data),
        message: backendResponse.message,
      };
    }
    return {
      success: false,
      error: backendResponse.error as BusinessError,
      message: backendResponse.message,
    };
  }

  /**
   * Map backend API response array to frontend API response array
   * @param backendResponse - Backend API response with array of shortened data
   * @param dataMapper - Function to map each item from backend to frontend format
   * @returns Frontend API response with array of full field names
   */
  static mapResponseArray<TBackend, TFrontend>(
    backendResponse: BackendApiResponse<TBackend[]>,
    dataMapper: (data: TBackend) => TFrontend
  ): ApiResponse<TFrontend[]> {
    if (backendResponse.success) {
      return {
        success: true,
        data: backendResponse.data.map(dataMapper),
        message: backendResponse.message,
      };
    } else {
      return {
        success: false,
        error: backendResponse.error as BusinessError,
        message: backendResponse.message,
      };
    }
  }

  /**
   * Map frontend data to backend format for API requests
   * @param frontendData - Frontend data with full field names
   * @param dataMapper - Function to map the data from frontend to backend format
   * @returns Backend data with shortened field names
   */
  static mapRequestData<TFrontend, TBackend>(
    frontendData: TFrontend,
    dataMapper: (data: TFrontend) => TBackend
  ): TBackend {
    return dataMapper(frontendData);
  }
}
